import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Component, OnInit, OnChanges, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { CoreApiService } from 'src/app/services/core-api.service';
import { PlanSummary } from 'src/app/models/plan-summary';
import { ActivatedRoute } from '@angular/router';
import { Utils } from '../services/utils.service';

import { MatCheckboxModule } from '@angular/material/checkbox';

import { PlanDetailsModalComponent } from '../plan-details-modal/plan-details-modal.component';

@Component({
    selector: 'app-plan-grid',
    templateUrl: './plan-grid.component.html',
    styleUrls: ['./plan-grid.component.scss'],
    providers: [{ provide: MatCheckboxModule, useValue: 'check'}],
})
export class PlanGridComponent implements OnInit, OnChanges {

/* * * * * * * *
Notes on integrating with plan-confirmation:
- Configure additionalColumns to display LoC-specific columns
* * * * * * * */

    /* ADDING A COLUMN
     * - Add the column name. This will likely be to additionalColumns - match to LoC that will have the new column
     * - Add column block in ng-container in HTML (feel free to copy-paste from defined column and change as needed)
     * - The matColumnDef parameter in the html MUST match the string added to additionalColumns
     * - Define the column data
     * - - New data will likely be benefit records. Add an array to selectedBenefitsKeys, matching the format defined above the variable
     * - - Code will automatically pull out a benefit with that name and place it in the selectedBenefits dictionary attached to each plan
     * - - The "Deductible" benefit is a good example to follow through
     * - If data is pulled from selectedBenefitsKeys, into selectedBenefits, sorting and filtering should Just Work (tm)
     * - If not, you'll need to modify the sorting/filtering logic to accomodate the new column
     * - - Sorting: tableConfigSort
     * - - Filtering: tableConfigFilter
     * - That should be all that's needed
     */


    @Output('selected') selectedEmitter = new EventEmitter<PlanSummary[]>();

    _planSummaries: {selectedPlanSummaries: PlanSummary[], offlinePlanSummaries: PlanSummary[]};

    @Input()
    set planSummaries(planSummaries: {selectedPlanSummaries: PlanSummary[], offlinePlanSummaries: PlanSummary[]}) {
        this._planSummaries = planSummaries;
        if (typeof planSummaries === 'undefined') {
            return;
        }
        this._planSummaries.selectedPlanSummaries.forEach(ps => {
            if (ps.isSelected) {
                this.selection.select(ps);
            }
        });
        this._planSummaries.offlinePlanSummaries.forEach(ps => {
            if (ps.isSelected) {
                this.selection.select(ps);
            }
        });
        this.selectedEmitter.emit(this.selection.selected);
    }

    private get planSummaryData() {
        let data: PlanSummary[] = [];
        if (this._planSummaries) {
            const selPlans = this._planSummaries.selectedPlanSummaries;
            if (selPlans && selPlans.length > 0) {
                data = data.concat(selPlans);
            }
            if (this._planSummaries.selectedPlanSummaries.length < 1) {
                this.showOfflinePlans = true;
                this.isDisabled = true;
            } else {
                this.isDisabled = false;
            }
            if (this.showOfflinePlans) {
                const offlinePlans = this._planSummaries.offlinePlanSummaries;
                if (offlinePlans && offlinePlans.length > 0) {
                    if (data !== null) {
                        data = data.concat(offlinePlans);
                    } else {
                        data = offlinePlans;
                    }
                    
                }
            }

        }

        return data;
    }

    get hasOfflinePlans() {
        return this._planSummaries && this._planSummaries.offlinePlanSummaries && this._planSummaries.offlinePlanSummaries.length > 0;
    }

    showOfflinePlans = false;

    isLoading = true;

    private _baseColumns: string[] = ['select', 'renewal', 'carrier', 'name', 'type'];
    private additionalColumns = {
        'Medical': ['premium', 'deductible'],
        'Dental': ['premium', 'deductible', 'maximum'],
        'Vision': [],
        'Life': [],
        'Disability': []
    };

    displayedColumns: string[] = [];

    get columnFilterOptions() {
        const cols = this.displayedColumns.slice(1);
        cols.forEach((c) => c = c[0].toUpperCase() + c.substring(1));
        return cols;
    }

    // [0] is the selectedBenefits key, [1] is the search-for string
    private selectedBenefitsKeys = [
        ['deductible', 'Deductible'],
        ['maximum', 'Annual Maximum']
    ];

    dataSource: MatTableDataSource<PlanSummary>;
    selection: SelectionModel<PlanSummary> = new SelectionModel<PlanSummary>(true, []);
    LoC: string;
    isDisabled: boolean;

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    constructor(private api: CoreApiService, private route: ActivatedRoute, public dialog: MatDialog, public utils: Utils) { }

    openPlanModal(plan: PlanSummary) {
        console.log('plan', plan);
        this.scrollToCenter();
        const dialogRef = this.dialog.open(PlanDetailsModalComponent, { data: { plan: plan, selections: this.selection } });
    }

    ngOnInit() {
        this.selection.changed.subscribe((sel) => {
            this.selectedEmitter.emit(this.selection.selected);
        });
    }

    ngOnChanges() {

        if (this._planSummaries) {
            this.isLoading = false;
            if (this._planSummaries.selectedPlanSummaries) {
                this._planSummaries.selectedPlanSummaries.forEach((p) => { this.buildSelectedBenefits.bind(this)(p); p.isOffline = false; });
            }
            if (this._planSummaries.offlinePlanSummaries) {
                this._planSummaries.offlinePlanSummaries.forEach((p) => { this.buildSelectedBenefits.bind(this)(p); p.isOffline = true; });
            }
        }

        const data = this.planSummaryData;
        if (data && data.length > 0) {
            this.LoC = data[0].lineOfCoverage;

            // pulls out the benefits that'll be displayed on the grid
            //    into their own dictionary, along with relevant sorting value
            this.processTableConfig();

            let toDisplay = this._baseColumns;
            if (this.LoC in this.additionalColumns) {
                toDisplay = toDisplay.concat(this.additionalColumns[this.LoC]);
            }
            if (this.hasOfflinePlans) {
                toDisplay = toDisplay.concat(['offline']);
            }
            this.displayedColumns = toDisplay;

        }
    }

    // This defines how the filter text field is treated
    // As of writing this (02/22/2019), the filter input is processed by:
    // - Removing any currency formatting
    // - Trimming off leading/trailing whitespace
    // - Making it lowercase
    // THEN the filter is applied
    // This filter is evaluated against every visible row of the dataSource, using the matching logic
    //    defined in tableConfigFilter
    applyFilter(filterValue: string) {
        this.dataSource.filter = this.stripCurrencyFormatting(filterValue).trim().toLowerCase();
    }

    filterOffline(isChecked: boolean) {
        this.showOfflinePlans = isChecked;
        const newData = this.planSummaryData;
        this.dataSource.data = newData;
        

        // pageIndex is 0-indexed, so this works without shifting indexes
        // this just makes it so, if the user is looking at a page that won't exist
        // because offline plans were removed, then they will be returned to the first page
        if (newData.length < this.paginator.pageSize * this.paginator.pageIndex) {
            this.paginator.firstPage();
        }
    }

        // Copy-pasted straight from https://material.angular.io/components/table/overview
    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllSelected() ?
                this.dataSource.filteredData.forEach(row => this.selection.deselect(row)) :
                this.dataSource.filteredData.forEach(row => this.selection.select(row));
    }

    processTableConfig() {
        this.dataSource = new MatTableDataSource<PlanSummary>(this.planSummaryData);
        this.dataSource.paginator = this.paginator;

        this.tableConfigFilter();
        this.tableConfigSort();
    }

    private buildSelectedBenefits(ps: PlanSummary) {
        const d: any = {};
        this.selectedBenefitsKeys.forEach(function(sb) {
            const e = ps.benefits.find(b => b.name === sb[1]);
            d[sb[0]] = [['N/A', null]];
            if (e) {
                d[sb[0]][0] = e.inValue;
                d[sb[0]][1] = e.inNumericValue;
            }
        });
        ps.selectedBenefits = d;
    }

    private tableConfigSort() {
        this.dataSource.sort = this.sort;

        // Custom sorting logic to control what the sort is based on for what column
        // will need updated based on benefits for other lines of coverage
        this.dataSource.sortingDataAccessor = (item: any, property: string) => {
            if (property in item.selectedBenefits) {
                return item.selectedBenefits[property][1];
            }
            // the below logic for select works, but the sort overrides the checkbox
            // so I've removed sortability for the select column in the html
            switch (property) {
                case 'carrier': return item.carrier.name;
                case 'select': return this.selection.selected.includes(item);
                case 'offline': return item.isOffline;
                default: return item[property];
            }
        };
    }

    private tableConfigFilter() {
        this.dataSource.filterPredicate = (data: PlanSummary, filter: string) => {

            if (!filter) {
                return true;
            }

            let compareTo = '';
            this.displayedColumns.forEach((col) => {
                if (col in data.selectedBenefits) {
                    // string comparison for filters, as opposed to number comp for sort
                    compareTo += data.selectedBenefits[col][0];
                }
                switch (col) {
                    case 'carrier': compareTo += data.carrier.name; compareTo += data.carrier.code; break;
                    case 'name': compareTo += data.name; break;
                    case 'type': compareTo += data.type; break;
                    case 'premium': compareTo += data.premium.toString(); break;
                    default: break; // Default is to leave it empty string, as initialized to
                }
            });

            // Remove the currency styling characters from the filter
            compareTo = this.stripCurrencyFormatting(compareTo).toLowerCase();
            return compareTo.includes(filter);
        };

    }

    // Instead of filtering on the monetary amount for money columns, filter on the text value
    // but remove the currency presentation characters so a filter on '1000' matches '$1,000'
    // There is probably a better way to do this?
    private stripCurrencyFormatting(t: string): string {
        return t.replace('$', '').replace(',', '');
    }

    private parseCanvasToken(signedRequest) {
        const win: any = window;
        const part = signedRequest.split('.')[1];
        const decodedToken = win.Sfdc.canvas.decode(part);
        const canvasToken = JSON.parse(decodedToken);
        return canvasToken;
    }

    private getCanvasToken() {
        const win: any = window;
        return new Promise((resolve, reject) => {
            win.Sfdc.canvas.client.refreshSignedRequest((data: any) => {
                if (data.status === 200) {
                    const signedRequest = data.payload.response;
                    const token = this.parseCanvasToken(signedRequest);
                    resolve(token);
                } else {
                    reject(new Error('Error occurred while getting canvas token'));
                }
            });
        });
    }

    // This script will scroll the page to the position where the modal is open.
    scrollToCenter() {
        const win: any = window;
        const sizes = win.Sfdc.canvas.client.size();
        const offSet = 600;
        const top = (sizes.heights.contentHeight / 2) - offSet;
        const args = JSON.stringify({ oeScrollToTop: true, top: top });
        return this.getCanvasToken().then((token: any) => {
            window.top.postMessage(args, token.client.targetOrigin);
        });
    }
}
